import React, { useState } from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

const API = getApiBaseUrl();

// 订单过期时间（分钟）
const ORDER_EXPIRE_MINUTES = 30;

// 简易充值中心（支持微信、Mock 支付）
const RechargeCenter = () => {
  const [points, setPoints] = useState(100);
  const [channel, setChannel] = useState(() => {
    try { return localStorage.getItem('recharge_channel') || 'alipay'; } catch { return 'alipay'; }
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);
  const [paidMsg, setPaidMsg] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [timeLeft, setTimeLeft] = useState(ORDER_EXPIRE_MINUTES * 60);
  const [pollMessage, setPollMessage] = useState('');
  const navigate = useNavigate();

  const createOrder = async () => {
    setError('');
    setPaidMsg('');
    setPollMessage('');
    setIsPolling(false);
    setTimeLeft(ORDER_EXPIRE_MINUTES * 60);
    setCreating(true);
    try {
      const res = await fetch(`${API}/payments/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ points: Number(points) || 0 })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || '创建订单失败');
      setOrder({ ...json, channel });

      // 如果是微信支付，自动启动轮询
      if (json.provider === 'wechat') {
        setTimeout(() => startPolling(json.order_id), 500);
      }
    } catch (e) {
      setError(e.message || '创建订单失败');
    } finally {
      setCreating(false);
    }
  };

  const mockPaid = async () => {
    if (!order) return;
    setError('');
    setPaidMsg('');
    try {
      const res = await fetch(`${API}/payments/mock/${order.order_id}/paid`, {
        method: 'POST',
        credentials: 'include'
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || '模拟支付失败');
      setPaidMsg('✅ 支付成功，积分已入账！');
      setOrder({ ...order, credited_at: new Date().toISOString() });
      // 3秒后自动跳转
      setTimeout(() => navigate('/points'), 3000);
    } catch (e) {
      setError(e.message || '模拟支付失败');
    }
  };

  // 轮询订单状态（微信支付用）
  const startPolling = async (orderId) => {
    if (!orderId) return;

    setIsPolling(true);
    setPollMessage('等待支付中...');
    let pollCount = 0;
    const maxPolls = ORDER_EXPIRE_MINUTES * 2; // 30分钟，每3秒查询一次

    const interval = setInterval(async () => {
      pollCount++;

      // 更新剩余时间显示
      setTimeLeft(prev => Math.max(0, prev - 3));

      if (pollCount > maxPolls) {
        clearInterval(interval);
        setIsPolling(false);
        setPollMessage('订单已过期，请重新创建');
        return;
      }

      try {
        const res = await fetch(`${API}/payments/orders/${orderId}`, {
          credentials: 'include'
        });
        const data = await res.json();

        if (data.status === 'credited') {
          clearInterval(interval);
          setIsPolling(false);
          setPaidMsg('✅ 支付成功，积分已入账！');
          setPollMessage('');
          setOrder(data);
          // 3秒后自动跳转
          setTimeout(() => navigate('/points'), 3000);
        } else {
          setPollMessage(`等待支付中... (${pollCount}/${maxPolls})`);
        }
      } catch (e) {
        console.error('轮询失败:', e);
        setPollMessage('轮询出错，请稍后重试');
      }
    }, 3000);
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">充值中心</h1>
          <p className="text-sm text-gray-500 mt-1">支持微信支付和模拟支付，请选择支付方式</p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded mb-4">{error}</div>
        )}

        <div className="bg-white rounded-lg shadow border p-4 mb-6">
          <div className="mb-3">
            <label className="block text-sm text-gray-600 mb-1">支付渠道</label>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="channel" checked={channel === 'alipay'} onChange={() => { setChannel('alipay'); try { localStorage.setItem('recharge_channel', 'alipay'); } catch {} }} disabled />
                <span className="text-gray-400">支付宝（建设中）</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="channel" checked={channel === 'wechat'} onChange={() => { setChannel('wechat'); try { localStorage.setItem('recharge_channel', 'wechat'); } catch {} }} />
                <span>微信</span>
              </label>
            </div>
          </div>
          <label className="block text-sm text-gray-600 mb-1">充值积分（1 元 = 1 积分，最少 1）</label>
          <input
            type="number"
            min="1"
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
          <button
            onClick={createOrder}
            disabled={creating}
            className="mt-3 px-4 py-2 rounded bg-emerald-600 text-white disabled:opacity-50"
          >
            {creating ? '创建中...' : '创建订单'}
          </button>
        </div>

        {order && (
          <div className="bg-white rounded-lg shadow border p-4 mb-6">
            <div className="text-gray-700 text-sm">订单号：{order.order_id}</div>
            <div className="text-gray-700 text-sm">金额：{order.amount} 元</div>
            <div className="text-gray-700 text-sm">积分：{order.points_to_credit}</div>
            <div className="text-gray-700 text-sm">支付方式：{order.provider === 'wechat' ? '微信支付' : order.provider === 'mock' ? '模拟支付' : '支付宝'}</div>

            {/* 微信支付 - 显示二维码 */}
            {order.provider === 'wechat' && order.payment_url && !order.credited_at && (
              <div className="mt-4 flex flex-col items-center">
                <p className="text-sm text-gray-600 mb-2 font-semibold">请使用微信扫描二维码支付</p>
                <div className="bg-white p-2 border-2 border-gray-300 rounded">
                  <QRCode
                    value={order.payment_url}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  剩余时间：{String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}
                </p>
              </div>
            )}

            {/* Mock模式 - 显示模拟支付按钮 */}
            {order.provider === 'mock' && !order.credited_at && (
              <div className="mt-4">
                <button
                  onClick={mockPaid}
                  className="w-full px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition"
                >
                  模拟支付完成
                </button>
              </div>
            )}

            {/* 轮询状态提示 */}
            {isPolling && (
              <div className="mt-3 flex flex-col items-center">
                <div className="inline-block">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
                <p className="text-sm text-blue-600 mt-1">{pollMessage}</p>
              </div>
            )}

            {/* 支付成功消息 */}
            {paidMsg && (
              <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-emerald-700 text-sm">
                {paidMsg}
              </div>
            )}

            {/* 过期消息 */}
            {pollMessage && !isPolling && pollMessage.includes('过期') && (
              <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {pollMessage}
              </div>
            )}

            {/* 操作按钮 */}
            <div className="mt-3 flex gap-2">
              {!isPolling && order.credited_at && (
                <button
                  onClick={() => navigate('/points')}
                  className="flex-1 px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition"
                >
                  查看积分
                </button>
              )}

              {!order.credited_at && (
                <button
                  onClick={() => {
                    setOrder(null);
                    setPaidMsg('');
                    setPollMessage('');
                    setTimeLeft(ORDER_EXPIRE_MINUTES * 60);
                    setIsPolling(false);
                  }}
                  className="flex-1 px-3 py-2 rounded border text-gray-600 hover:bg-gray-50 transition"
                >
                  取消
                </button>
              )}
            </div>

            {paidMsg && (
              <div className="mt-2 text-center text-xs text-gray-500">
                3秒后自动跳转...
              </div>
            )}
          </div>
        )}

        <div className="text-sm text-gray-500">提示：微信支付时请用微信扫描二维码。</div>
      </div>
    </div>
      <Footer variant="simple" showCopyright={false} />
    </>
  );
};

export default RechargeCenter;
